"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { useVariableContext } from "../../contexts/VariableContext";
import { useComposeClientContext } from "@/lib/contexts/compose/ComposeClientContext";
import { useComposeFormStateContext } from "@/lib/contexts/compose/ComposeFormStateContext";

type VariableCounterProps = Record<string, never>;

/**
 * Simple variable counter component
 * Job: Show how many variables are filled/unfilled
 */
export function VariableCounter(_props?: VariableCounterProps) {
  const { getAllVariables, hasValue } = useVariableContext();
  const formState = useComposeFormStateContext();
  const clientState = useComposeClientContext();

  const { watchedSubject, watchedBody } = formState;
  const { selectedClient } = clientState;

  const allVariables = getAllVariables(watchedSubject, watchedBody);
  const filledCount = allVariables.filter((varName: string) =>
    hasValue(varName, selectedClient)
  ).length;
  const unfilledCount = allVariables.length - filledCount;

  if (allVariables.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      {filledCount > 0 && (
        <Badge variant="outline" className="text-green-600 border-green-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          {filledCount} filled
        </Badge>
      )}
      {unfilledCount > 0 && (
        <Badge variant="outline" className="text-red-600 border-red-300">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {unfilledCount} required
        </Badge>
      )}
    </div>
  );
}
